/**
 * Provides functions for working with the Font Service API
 *
 * http://swagger.na.sas.com/apis/fonts/v3/redoc.html
 */

/**
 * Describes a link to a font resource
 */
interface FontLink {
  uri: string;
  rel: string;
  type: string;
}

/**
 * Describes a single font. This is a subset of application/vnd.sas.font+json
 */
export interface FontDefinition {
  name?: string;
  family?: string;
  postScriptName?: string;
  weight?: 'normal' | 'bold';
  usWeightClass?: number;
  style?: 'normal' | 'italic';
  format?: 'woff' | 'ttf' | 'ttc';
  links?: FontLink[];
}

/**
 * Data returned from the /fonts/fonts endpoint. This is a subset of application/vnd.sas.collection+json
 */
interface FontCollection {
  items?: FontDefinition[];
}

/**
 * Fetches font definitions from the server.
 */
export async function fetchFontDefinitions(serverUrl: string): Promise<FontDefinition[]> {
  // `limit` is needed because its default value is 10, and there
  // are about 100 fonts that we need to add.
  // `filter` value copied from nova-commons
  const fontsUrl = `${serverUrl}/fonts/fonts?filter=and(eq(privateFlag,%20%27false%27),%20ne(format,%20%27ttc%27))&limit=1000`;
  const response = await fetch(fontsUrl, {
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok || response.url.includes('/SASLogon/')) {
    return Promise.reject('Unable to fetch fonts');
  }
  const fontDefinitions = (await response.json()) as FontCollection;
  return fontDefinitions.items ?? [];
}
